/**
 * CLEARO accessory-set upgrade — <clearo-upsell>
 * Replaces the "Package" pill group with one tickable card. Ticking/unticking
 * clicks the real option input, so Origin's variant logic (price, URL, cart,
 * sticky bar) does the rest. Without JS the normal pills stay visible.
 * Markup: snippets/clearo-upsell.liquid
 */
(() => {
  if (customElements.get('clearo-upsell')) return;

  const formatMoney = (cents, format) => {
    const amount = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  };

  class ClearoUpsell extends HTMLElement {
    connectedCallback() {
      this.info = this.closest('.product__info-container') || document;
      this.optionName = this.dataset.option;
      this.position = parseInt(this.dataset.position, 10) || 0;
      this.base = this.dataset.base;
      this.upgrade = this.dataset.upgrade;
      this.format = this.dataset.moneyFormat || '{{amount}}';
      try {
        this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      } catch (e) {
        return;
      }
      this.toggle = this.querySelector('[data-toggle]');
      this.extraEl = this.querySelector('[data-extra]');
      if (!this.findGroup()) return;

      this.hidden = false;
      // The accessory set is a free gift: it is always part of the order
      this.autoTries = 0;
      this.toggle.addEventListener('change', () => this.select(this.toggle.checked ? this.upgrade : this.base));

      // Photo of the accessory set opens in a larger view
      this.dialog = this.querySelector('[data-gift-dialog]');
      const open = this.querySelector('[data-gift-open]');
      if (this.dialog && open) {
        open.addEventListener('click', () => this.dialog.showModal && this.dialog.showModal());
        this.dialog.addEventListener('click', (e) => {
          if (e.target === this.dialog || e.target.closest('[data-gift-close]')) this.dialog.close();
        });
      }

      // Origin re-renders the picker after each change: keep in sync
      this.observer = new MutationObserver((records) => {
        // ignore changes made inside this card (avoids reacting to our own updates)
        if (records.every((r) => this.contains(r.target))) return;
        this.sync();
      });
      this.observer.observe(this.info, { subtree: true, childList: true, attributes: true, attributeFilter: ['checked'] });
      this.info.addEventListener('change', () => setTimeout(() => this.sync(), 60));
      this.sync();
    }

    disconnectedCallback() {
      this.observer && this.observer.disconnect();
    }


    // The fieldset for the "Package" option
    findGroup() {
      const groups = Array.from(this.info.querySelectorAll('.product-form__input'));
      // By position first (works in every language), then by name
      this.group =
        (this.position && groups.length >= this.position && groups[this.position - 1]) ||
        groups.find((g) => {
          const legend = g.querySelector('legend, label.form__label');
          return legend && legend.textContent.trim().startsWith(this.optionName);
        });
      if (this.group && !this.group.classList.contains('clr-up__hidden-group')) this.group.classList.add('clr-up__hidden-group');
      return this.group;
    }

    optionIndex() {
      const groups = Array.from(this.info.querySelectorAll('.product-form__input'));
      return groups.indexOf(this.group);
    }

    select(value) {
      this.findGroup();
      const input = Array.from(this.group.querySelectorAll('input[type="radio"]')).find((i) => i.value === value);
      if (input && !input.checked) input.click();
    }

    sync() {
      if (!this.findGroup()) return;
      const checked = Array.from(this.info.querySelectorAll('.product-form__input input[type="radio"]:checked')).map((i) => i.value);
      const idx = this.optionIndex();
      const isUpgrade = checked[idx] === this.upgrade;
      if (this.toggle.checked !== isUpgrade) this.toggle.checked = isUpgrade;

      // Price difference for the currently selected other options
      const pick = (value) => {
        const wanted = checked.slice();
        wanted[idx] = value;
        return this.variants.find((v) => v.options.every((o, i) => o === wanted[i]));
      };
      const baseV = pick(this.base);
      const upV = pick(this.upgrade);

      // Free gift: make sure the accessory-set variant is the one selected
      if (!isUpgrade && upV && upV.available && this.autoTries < 6) {
        this.autoTries += 1;
        this.select(this.upgrade);
        return;
      }

      // Gift row: show "was X, now Y" when the selected variant has a compare-at price
      if (upV) {
        const deal = this.querySelector('[data-gift-deal]');
        const plain = this.querySelector('[data-gift-plain]');
        if (deal && plain) {
          const hasDeal = upV.compare > upV.price;
          if (hasDeal) {
            deal.querySelector('[data-was]').textContent = formatMoney(upV.compare, this.format);
            deal.querySelector('[data-now]').textContent = formatMoney(upV.price, this.format);
            deal.querySelectorAll('[data-save]').forEach((el) => {
              el.textContent = formatMoney(upV.compare - upV.price, this.format);
            });
          }
          deal.hidden = !hasDeal;
          plain.hidden = hasDeal;
        }
      }

      if (baseV && upV) {
        const diff = upV.price - baseV.price;
        const extra = diff > 0 ? `+${formatMoney(diff, this.format)}` : '';
        if (this.extraEl.textContent !== extra) this.extraEl.textContent = extra;
        if (this.toggle.disabled !== !upV.available) this.toggle.disabled = !upV.available;
        if (this.classList.contains('is-unavailable') !== !upV.available) this.classList.toggle('is-unavailable', !upV.available);
      }
    }
  }

  customElements.define('clearo-upsell', ClearoUpsell);
})();
